import { Reveal } from "./Reveal";
import { Eye, Shapes, Compass } from "lucide-react";
import { PerspectiveDiagram } from "./PerspectiveDiagram";
import { ScaleRuler } from "./ScaleRuler";

const values = [
  {
    icon: Eye,
    tag: "What I do",
    title: "I notice systems & patterns",
    body: "Before form, there is structure. I read the hidden order in a brief: the flows, constraints, and rhythms beneath it.",
  },
  {
    icon: Shapes,
    tag: "How I think",
    title: "I translate complexity into form",
    body: "Insight only matters when it's legible. I turn tangled systems into something clear, usable, and quietly beautiful.",
  },
  {
    icon: Compass,
    tag: "What I create",
    title: "One instinct across mediums",
    body: "Buildings, tools, research, product, ink. Different surfaces, one design instinct carrying through all of them.",
  },
];

export const ValueSection = () => {
  return (
    <section id="approach" data-testid="value-section" className="relative bg-[#FFFFFF] py-16 sm:py-36">
      <div className="max-w-6xl mx-auto px-6 sm:px-10">
        <Reveal>
          <p className="font-sans text-xs tracking-[0.32em] uppercase text-[#C9A24B]">
            02 · Why it matters
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.1] text-ink mt-4 max-w-2xl">
            One practice, unified by one way of seeing.
          </h2>
        </Reveal>

        <div className="grid md:grid-cols-3 gap-6 lg:gap-8 mt-16">
          {values.map((v, i) => {
            const Icon = v.icon;
            return (
              <Reveal key={v.title} delay={i * 0.1}>
                <div
                  data-testid={`value-card-${i}`}
                  className="group h-full bg-[#FFFFFF] border border-[#1A1A1A]/10 rounded-2xl p-8 transition-all duration-500 hover:-translate-y-1.5 hover:border-[#6B8F71] hover:shadow-[0_18px_40px_-24px_rgba(74,93,78,0.5)]"
                >
                  <div className="w-12 h-12 rounded-full border border-[#6B8F71] flex items-center justify-center text-[#6B8F71] transition-colors duration-500 group-hover:bg-[#6B8F71] group-hover:text-[#FFFFFF]">
                    <Icon size={20} strokeWidth={1.5} />
                  </div>
                  <p className="font-sans text-[11px] tracking-[0.28em] uppercase text-[#C9A24B] mt-7">
                    {v.tag}
                  </p>
                  <h3 className="font-serif text-2xl text-ink mt-2 leading-snug">
                    {v.title}
                  </h3>
                  <p className="font-sans text-sm text-ink leading-relaxed mt-4">
                    {v.body}
                  </p>
                </div>
              </Reveal>
            );
          })}
        </div>

        {/* points of view — the full picture comes from every angle */}
        <div className="mt-16 sm:mt-32 grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          <Reveal>
            <div>
              <p className="font-sans text-xs tracking-[0.32em] uppercase text-[#C9A24B]">
                How I get the full picture
              </p>
              <h3 className="font-serif text-3xl sm:text-4xl leading-[1.1] text-ink mt-4 max-w-md">
                One situation, seen from every point of view.
              </h3>
              <p className="font-sans text-base text-ink leading-relaxed mt-5 max-w-md">
                The same problem looks different depending on where you stand. Every
                system has its own set of vantage points, and they shift from one
                project to the next, so I borrow each relevant one before deciding.
                Every angle shows a single face; together they reveal the complete
                picture.
              </p>
            </div>
          </Reveal>
          <div className="py-6">
            <PerspectiveDiagram />
            <p className="text-center font-mono text-[10px] tracking-[0.24em] uppercase text-[#C9A24B] mt-5">
              complete picture
            </p>
          </div>
        </div>

        {/* one instinct, applied across scales — urban → buildings → spaces → products */}
        <div className="mt-16 sm:mt-32">
          <Reveal>
            <p className="font-sans text-xs tracking-[0.32em] uppercase text-[#C9A24B]">
              Across scales
            </p>
            <h3 className="font-serif text-3xl sm:text-4xl leading-[1.1] text-ink mt-4 max-w-lg">
              One design instinct, from the city to the human.
            </h3>
            <p className="font-sans text-base text-ink leading-relaxed mt-5 max-w-xl">
              The same way of reading systems holds at every scale. I've built that
              instinct through buildings, spaces, and products, and it reaches up to
              the urban scale too. As the scale tightens, the principles carry
              through, and every decision at one scale informs the next.
            </p>
          </Reveal>
          <div className="mt-12 sm:mt-16">
            <ScaleRuler />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ValueSection;
